import React, { useState, useRef, useEffect } from 'react';

const MonthYearPicker = ({
  label,
  value,
  onChange,
  placeholder = 'Select month and year',
  required = false,
  disabled = false,
  className = '',
  error,
  minYear = 2000,
  maxYear = 2025,
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(value?.month || '');
  const [selectedYear, setSelectedYear] = useState(value?.year || '');
  const [isSelectingYear, setIsSelectingYear] = useState(true);
  const dropdownRef = useRef(null);

  // Get current date for validation
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const years = Array.from({ length: maxYear - minYear + 1 }, (_, i) => ({
    value: (minYear + i).toString(),
    label: (minYear + i).toString(),
  }));

  // Get available months based on selected year
  const getAvailableMonths = (year) => {
    if (parseInt(year) === currentYear) {
      // For current year, only show months up to current month
      return months.filter(month => parseInt(month.value) <= currentMonth);
    } else if (parseInt(year) < currentYear) {
      // For past years, show all months
      return months;
    } else {
      // For future years, don't show any months (invalid selection)
      return [];
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsSelectingYear(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (value) {
      setSelectedMonth(value.month || '');
      setSelectedYear(value.year || '');
    }
  }, [value]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setIsSelectingYear(true);
      }
    }
  };

  const handleYearSelect = (year) => {
    setSelectedYear(year);
    setSelectedMonth('');
    setIsSelectingYear(false);
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    const newValue = { month, year: selectedYear };
    onChange(newValue);
    setIsOpen(false);
    setIsSelectingYear(true);
  };

  const handleBackToYear = () => {
    setIsSelectingYear(true);
    setSelectedMonth('');
  };

  const getDisplayValue = () => {
    if (selectedMonth && selectedYear) {
      const monthLabel = months.find(m => m.value === selectedMonth)?.label;
      return `${monthLabel} ${selectedYear}`;
    }
    return placeholder;
  };

  const getMonthLabel = (monthValue) => {
    return months.find(m => m.value === monthValue)?.label || monthValue;
  };

  const availableMonths = getAvailableMonths(selectedYear);

  return (
    <div className={`relative ${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled}
          className={`
            w-full px-4 py-3 text-left bg-white border-2 rounded-xl shadow-sm
            focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500
            disabled:bg-gray-100 disabled:cursor-not-allowed
            transition-all duration-300 ease-out
            transform hover:scale-[1.01] focus:scale-[1.01]
            ${error
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20'
              : 'border-gray-200 hover:border-gray-300'
            }
            ${isOpen ? 'border-red-500 ring-4 ring-red-500/20' : ''}
          `}
          {...props}
        >
          <div className="flex items-center justify-between">
            <span className={selectedMonth && selectedYear ? 'text-gray-900' : 'text-gray-500'}>
              {getDisplayValue()}
            </span>
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg">
            {/* Year Selection */}
            {isSelectingYear && (
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Select Year</h3>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                  {years.map((year) => (
                    <button
                      key={year.value}
                      onClick={() => handleYearSelect(year.value)}
                      className={`
                        px-3 py-2 text-sm rounded-lg transition-all duration-200
                        ${selectedYear === year.value
                          ? 'bg-red-100 text-red-900 font-medium'
                          : 'text-gray-700 hover:bg-gray-100'
                        }
                      `}
                    >
                      {year.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Month Selection */}
            {!isSelectingYear && (
              <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={handleBackToYear}
                    className="flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Year
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                
                <div className="mb-3">
                  <p className="text-sm text-gray-600 mb-2">
                    Selected Year: <span className="font-medium text-gray-900">{selectedYear}</span>
                  </p>
                  {parseInt(selectedYear) === currentYear && (
                    <p className="text-xs text-gray-500 mb-2">
                      Only months up to {getMonthLabel(currentMonth.toString().padStart(2, '0'))} are available for {currentYear}
                    </p>
                  )}
                  {parseInt(selectedYear) > currentYear && (
                    <p className="text-xs text-red-500 mb-2">
                      Cannot select future years
                    </p>
                  )}
                </div>
                
                {availableMonths.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {availableMonths.map((month) => (
                      <button
                        key={month.value}
                        onClick={() => handleMonthSelect(month.value)}
                        className={`
                          px-3 py-2 text-sm rounded-lg transition-all duration-200
                          ${selectedMonth === month.value
                            ? 'bg-red-100 text-red-900 font-medium'
                            : 'text-gray-700 hover:bg-gray-100'
                          }
                        `}
                      >
                        {month.label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 text-sm">
                      {parseInt(selectedYear) > currentYear 
                        ? 'Cannot select months for future years'
                        : 'No months available for this year'
                      }
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="mt-2 animate-shake">
          <p className="text-sm text-red-600 flex items-center bg-red-50 px-3 py-2 rounded-xl border border-red-200">
            <svg
              className="w-4 h-4 mr-2 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            {error}
          </p>
        </div>
      )}
    </div>
  );
};

export default MonthYearPicker;
